import { Avatar, Box, Grid, GridProps, Stack, styled, Typography } from '@mui/material';
import { IconCalendar, IconClock, IconUser2 } from 'assets/img';
import {
  PopoverTwoColumns,
  PopoverTwoColumnsInfoRow,
} from 'redesign/popover-two-column-table/popover-two-columns';
import { getFullNameWithInfix } from 'hq/components/views/flexWorkers/utils';
import { DateTime } from 'luxon';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AvailabilityParsedItem } from '../availability-calendar.types';

export const AvailabilityGridCell = ({
  availabilityBlocks,
}: {
  availabilityBlocks: AvailabilityParsedItem[];
}) => {
  const blockHasAvailabilitySegment =
    availabilityBlocks.length > 0 && availabilityBlocks.some((block) => block.type === 'available');

  return (
    <StyledContainer container direction="column" spacing={1} wrap="nowrap">
      {blockHasAvailabilitySegment ? (
        <StyledAvailabilityCell
          data={availabilityBlocks}
          item
          data-testid={`availability-block-${availabilityBlocks[0].workerForPopover?.workerId}`}
        />
      ) : (
        <Grid item />
      )}
      <Grid item />
      <Grid item />
    </StyledContainer>
  );
};

const AvailabilityCell = ({ data, ...props }: { data: any } & GridProps) => {
  const popoverInfoRows = getPopoverInfoRows(data);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { t } = useTranslation('all-platforms-common');
  const closeTimer = useRef<number | null>(null);

  const startHoverEffect = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setAnchorEl(e.currentTarget);
  };

  const stopHoverEffect = () => {
    closeTimer.current = window.setTimeout(() => {
      setAnchorEl(null);
    }, 100);
  };

  return (
    <>
      <Grid
        {...props}
        onMouseEnter={startHoverEffect}
        onMouseLeave={stopHoverEffect}
        sx={{ height: '100%', width: '100%' }}
      />
      <PopoverTwoColumns
        title={t('availability_page.worker_available')}
        data={popoverInfoRows}
        maxWidth={350}
        maxHeight={400}
        anchorEl={anchorEl}
      />
    </>
  );
};

const StyledAvailabilityCell = styled(AvailabilityCell)(({ theme }) => ({
  backgroundColor: 'rgba(202, 209, 248)',
  ':hover': {
    backgroundColor: 'rgba(182, 189, 228)',
    border: '1px solid',
    borderColor: theme.palette.lwPrimary?.[5],
  },
  padding: 0,
  width: '100%',
  margin: 0,
  marginTop: 15,
  marginLeft: 4,
  height: '100%',
}));

const StyledContainer = styled(Grid)({
  height: '40px',
  width: '100%',
  boxSizing: 'border-box',
  flexWrap: 'nowrap',
});

export const getPopoverInfoRows = (data: AvailabilityParsedItem[]): PopoverTwoColumnsInfoRow[] => {
  const rows: PopoverTwoColumnsInfoRow[] = [];

  const startDate = DateTime.fromISO(data[0].utcStartDateTime).toLocal();

  rows.push({
    label: 'Naam',
    labelIcon: <IconUser2 />,
    value: (
      <Stack
        direction="row"
        justifyContent={'flex-end'}
        spacing={1}
        alignItems="center"
        alignContent={'center'}
        width={'100%'}
      >
        <StyledAvatarContainer>
          <Avatar src={data[0].workerForPopover?.profilePictureUrl} alt="Profile Picture" />
        </StyledAvatarContainer>
        <Typography
          variant="body1"
          fontWeight={600}
          noWrap
          maxWidth={300}
          alignSelf={'center'}
          textOverflow={'ellipsis'}
        >
          {getFullNameWithInfix(data[0].workerForPopover)}
        </Typography>
      </Stack>
    ),
  });

  rows.push({
    label: 'Datum',
    labelIcon: <IconCalendar />,
    value: startDate.toFormat('d LLL'),
  });

  const timeRanges = data.map((item) => {
    const from = DateTime.fromISO(item.utcStartDateTime).toLocal();
    const to = DateTime.fromISO(item.utcEndDateTime).toLocal();
    if (from.hour === 0 && from.minute === 0 && to.hour === 23 && to.minute === 59) {
      return `Hele dag`;
    }
    return `${from.toFormat('HH:mm')}–${to.toFormat('HH:mm')}`;
  });

  rows.push({
    label: 'Tijd',
    labelIcon: <IconClock />,
    value: (
      <Stack spacing={0.5}>
        {timeRanges.map((range, i) => (
          <Typography key={i} variant="body1" fontWeight={600}>
            {range}
          </Typography>
        ))}
      </Stack>
    ),
  });

  return rows;
};

const StyledAvatarContainer = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
}));
